import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  Chip,
  Grid2 as Grid,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
} from '@mui/material'
import { useSnackbar } from 'notistack'
import React, { useContext, useEffect, useMemo, useState } from 'react'
import InputMask from 'react-input-mask'

import { AppContext } from 'providers/context/App'
import { AppointmentConfirmationContext } from 'providers/context/AppointmentConfirmation'
import { ErrorContext, ErrorInitialData } from 'providers/context/Error'
import { deletePaymentMethods, getPaymentMethods } from 'services/Payment'
import { AMEX_CARD, NON_AMEX_CARD } from 'utils/constants/Helpers'
import {
  GENERAL_ERROR_MESSAGE,
  PAYMENT_MESSAGES,
} from 'utils/constants/Messages'
import { cardTypeValidation, conditionalClasses } from 'utils/helper-functions'
import {
  getBrandImage,
  getBrandName,
  getBrandNameConverter,
} from 'utils/helper-functions/cardBrands'
import {
  CARD_REMOVE_FAILED,
  FETCH_USER_INFO_FAILED,
} from 'utils/sentry/sentry-messages'
import { captureError } from 'utils/sentry/sentry-utils'
import { NewCardFormState, PaymentMethod } from 'utils/types/appointmentTypes'
import CustomDialog from '../dialog'
import AppointmentPromo from './appointment-promo'
import CardExpireText from './cardExpireText'

import { ReactComponent as AlertIcon } from 'assets/icons/alert-circle.svg'
import { ReactComponent as GreenTick } from 'assets/icons/green-tick.svg'
import { ReactComponent as PlusCircleIcon } from 'assets/icons/plus-button-outline-new.svg'
import { ReactComponent as TrashIcon } from 'assets/icons/red-trash.svg'
import './appointment.scss'

type Props = {
  isMembership?: boolean
}

const AppointmentPayment = ({ isMembership }: Props) => {
  const { updateErrorData } = useContext(ErrorContext)
  const { updateData } = useContext(AppointmentConfirmationContext)
  const { appMasterData } = useContext(AppContext)
  const { enqueueSnackbar } = useSnackbar()
  const [selectedCard, setSelectedCard] = useState('')
  const [cards, setCards] = useState<PaymentMethod[]>([])
  const [showAddCard, setShowAddCard] = useState(false)
  const [isShow, setIsShow] = useState(true)
  const [amex, setAmex] = useState(false)
  const [isOpen, setIsOpen] = useState(false)
  const [cardId, setCardId] = useState('')
  const [values, setValues] = useState<NewCardFormState>({
    name: '',
    cardNumber: '',
    expiryMonthYear: '',
    cvv: '',
    zipCode: '',
  })

  const removeCard = async () => {
    try {
      const res = await deletePaymentMethods(cardId)
      if (res) {
        enqueueSnackbar(PAYMENT_MESSAGES.CARD_DELETE_SUCCESS, {
          variant: 'success',
        })
        await getPaymentInfo()
        setIsOpen(false)
      }
    } catch (error) {
      captureError(CARD_REMOVE_FAILED, {
        email: appMasterData.userInfo.email,
        cardId: cardId,
        error,
        functionName: 'removeCard',
      })
      enqueueSnackbar(GENERAL_ERROR_MESSAGE, { variant: 'error' })
    }
  }

  const handlePopupOpen = (id: string) => {
    setIsOpen(true)
    setCardId(id)
  }

  const onSelectCard = (event: SelectChangeEvent) => {
    setSelectedCard(event.target.value)
    if (event.target.value) {
      setShowAddCard(false)
      updateData({
        selectedCardToken: event.target.value,
        isExistingCard: true,
        selectedCard: cards.find((card) => card.token === selectedCard),
      })
    } else {
      setShowAddCard(true)
      updateData({ selectedCardToken: '', isExistingCard: false })
    }
  }

  const getPaymentInfo = async () => {
    try {
      const paymentMethods =
        !!appMasterData.user.token && (await getPaymentMethods())
      if (paymentMethods && paymentMethods.length > 0) {
        const selectedToken = paymentMethods[0].token
        setShowAddCard(false)
        setSelectedCard(selectedToken)
        updateData({
          isExistingCard: true,
          selectedCardToken: selectedToken,
          selectedCard: paymentMethods[0],
        })
        setIsShow(true)
        setCards(paymentMethods)
      } else {
        setIsShow(false)
        setSelectedCard('')
        setShowAddCard(true)
        updateData({
          isExistingCard: false,
          selectedCardToken: '',
        })
      }
    } catch (error) {
      const e = error as ErrorInitialData
      captureError(FETCH_USER_INFO_FAILED, {
        email: appMasterData.userInfo.email,
        error,
        functionName: 'getPaymentInfo',
      })
      updateErrorData(e)
      setShowAddCard(true)
    }
  }

  const handleChange =
    (prop: keyof NewCardFormState) =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      if (prop === 'cardNumber') {
        cardValidation()
      }
      const newValues: any = { ...values, [prop]: event.target.value }
      if (
        newValues.name &&
        newValues.cardNumber &&
        newValues.expiryMonthYear &&
        newValues.cvv &&
        newValues.zipCode
      ) {
        updateData({ newCardDisableStatus: false })
      } else {
        updateData({ newCardDisableStatus: true })
      }
      setValues(newValues)
    }

  const setContextCardValue = () => {
    const cardNumber = values.cardNumber.replace(/[\s_+]/g, '')
    const expMonthYear = values.expiryMonthYear.split('/').map((d) => Number(d))
    const contextVal = {
      name: values.name,
      number: cardNumber,
      cvv: values.cvv,
      exp_month: expMonthYear[0],
      exp_year: expMonthYear[1],
      brand: cardNumber ? getBrandNameConverter(getBrandName(cardNumber)) : '',
      zip_code: values.zipCode,
    }
    updateData({ newCard: contextVal })
  }

  useEffect(() => {
    getPaymentInfo().catch((e) => console.error(e))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appMasterData.isLoggedIn])

  useEffect(() => {
    setContextCardValue()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values])

  const cardValidation = () => {
    const cardType = cardTypeValidation(values?.cardNumber)
    setAmex(cardType === 'AMEX')
  }

  const isPaymentAccordionExpanded = useMemo(() => {
    return (
      appMasterData.isLoggedIn && Boolean(appMasterData?.userInfo?.phone_number)
    )
  }, [appMasterData.isLoggedIn, appMasterData?.userInfo?.phone_number])

  return (
    <>
      <div
        className={conditionalClasses(
          Boolean(isMembership),
          'membership-payment-section',
          '',
          'payment-section'
        )}
      >
        <Accordion expanded={isPaymentAccordionExpanded}>
          <AccordionSummary aria-controls="panel1a-content" id="panel1a-header">
            <div className="payment-head">
              <Chip label="2" />
              Payment Method
            </div>
          </AccordionSummary>
          <AccordionDetails>
            <div className="payment-text body-2">
              {isMembership
                ? 'Enter the credit or debit card to be used for your monthly billing.'
                : 'Your credit card is used to hold your appointment and will only be charged after your appointment.'}
            </div>
            {!!cards && cards.length > 0 && isShow && (
              <Select
                className={
                  isMembership ? 'membership-card-selection' : 'card-selection'
                }
                value={selectedCard}
                onChange={onSelectCard}
                displayEmpty
                IconComponent={KeyboardArrowDownIcon}
                inputProps={{ 'aria-label': 'Without label' }}
                MenuProps={{
                  className: conditionalClasses(
                    Boolean(isMembership),
                    'membership-card-items',
                    'card-items'
                  ),
                }}
              >
                {cards.map((card) => {
                  return (
                    <MenuItem
                      value={card.token}
                      divider
                      key={card.id}
                      className="menu-item"
                    >
                      <div className="selected-card">
                        <img
                          src={getBrandImage(card?.brand ?? card.number)}
                          alt=""
                          loading="lazy"
                          className="card-logo"
                        />
                        <span className="body-1 card-num">
                          •••• {card.number.substring(card.number.length - 4)}
                        </span>
                        <span>•</span>
                        <CardExpireText card={card} />
                      </div>
                      <div>
                        {selectedCard === card.token && (
                          <div className="check-icon">
                            <GreenTick />
                          </div>
                        )}
                        {selectedCard !== card.token && (
                          <div className="trash-icon">
                            <TrashIcon
                              onClick={() => {
                                handlePopupOpen(card.id)
                              }}
                            />
                          </div>
                        )}
                        {new Date(card.exp_year, card.exp_month, 1) <
                          new Date() && (
                          <div className="alert-icon">
                            <AlertIcon />
                          </div>
                        )}
                      </div>
                    </MenuItem>
                  )
                })}
                <MenuItem value="" divider>
                  <PlusCircleIcon className="new-card-icon" />{' '}
                  <span className="addcard-text">Add New Card</span>
                </MenuItem>
              </Select>
            )}

            {showAddCard && (
              <div className="add-newcard-form">
                <Grid container spacing={3}>
                  <Grid
                    size={{
                      xs: 12,
                    }}
                  >
                    <TextField
                      label="Name on Card"
                      variant="filled"
                      fullWidth
                      value={values.name}
                      onChange={handleChange('name')}
                    />
                  </Grid>
                  <Grid size={{ xs: 12 }}>
                    <InputMask
                      mask={amex ? AMEX_CARD : NON_AMEX_CARD}
                      alwaysShowMask={false}
                      value={values.cardNumber}
                      onChange={handleChange('cardNumber')}
                    >
                      {/* @ts-ignore */}
                      {() => (
                        <TextField
                          label="Card Number"
                          variant="filled"
                          fullWidth
                        />
                      )}
                    </InputMask>
                  </Grid>
                  <Grid size={{ xs: 6 }}>
                    <InputMask
                      mask="99 / 9999"
                      value={values.expiryMonthYear}
                      onChange={handleChange('expiryMonthYear')}
                    >
                      {/* @ts-ignore */}
                      {() => (
                        <TextField label="MM/YYYY" variant="filled" fullWidth />
                      )}
                    </InputMask>
                  </Grid>
                  <Grid size={{ xs: 6 }}>
                    <InputMask
                      mask={amex ? '9999' : '999'}
                      onChange={handleChange('cvv')}
                      value={values.cvv}
                    >
                      {/* @ts-ignore */}
                      {() => (
                        <TextField label="CVV" variant="filled" fullWidth />
                      )}
                    </InputMask>
                  </Grid>
                  <Grid size={{ xs: 12 }}>
                    <TextField
                      label="Zip Code"
                      variant="filled"
                      fullWidth
                      value={values.zipCode}
                      onChange={handleChange('zipCode')}
                      slotProps={{
                        htmlInput: {
                          maxLength: 5,
                        },
                      }}
                    />
                  </Grid>
                </Grid>
              </div>
            )}
            {!isMembership && (
              <>
                <hr className="custom-divider" />
                <AppointmentPromo />
              </>
            )}
          </AccordionDetails>
        </Accordion>
      </div>
      <CustomDialog isModalOpen={isOpen} handleClose={() => setIsOpen(false)}>
        <>
          <h4 className="dialog-title">Are you sure?</h4>
          <span className="dialog-content body-1">
            Once you delete this card it cannot be recovered.
          </span>
          <div className="btn-section">
            <Button
              onClick={() => {
                removeCard().catch((e) => console.error(e))
              }}
              className="red-btn"
            >
              Delete Card
            </Button>
            <Button
              onClick={() => setIsOpen(false)}
              autoFocus
              className="red-btn red-btn-outlined body-1"
            >
              Keep Card
            </Button>
          </div>
        </>
      </CustomDialog>
    </>
  )
}

export default React.memo(AppointmentPayment)
